import React, { useRef, useEffect } from 'react';
import { StyleSheet, Pressable, Animated, View, Easing, Platform, Alert, PermissionsAndroid } from 'react-native';
import { requestRecordingPermissionsAsync } from 'expo-audio';
import { useVoiceModeStore } from '@/store';
import Tooltip from '@/components/Tooltip';
import { useTheme } from '@/context/ThemeContext';
import useTranslation from '@/hooks/useTranslation';
import useRequireAuth from '@/hooks/useRequireAuth';
import { requirePlusForVoice } from '@/lib/requirePlusForVoice';
import { isDraftComposerConversationId } from './composerActionSlot';

const BUTTON_SIZE = 38;
const INNER_SIZE = 32;

const styles = StyleSheet.create({
  button: {
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    position: 'relative',
  },
  buttonDisabled: {
    opacity: 0.4,
    ...(Platform.OS === 'web' && { cursor: 'not-allowed' }),
  },
  pulseRing: {
    position: 'absolute',
    width: BUTTON_SIZE,
    height: BUTTON_SIZE,
    borderRadius: BUTTON_SIZE / 2,
    borderWidth: 1.5,
  },
  innerContainer: {
    width: INNER_SIZE,
    height: INNER_SIZE,
    borderRadius: INNER_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  soundWaveContainer: {
    position: 'absolute',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  },
  soundBar: {
    width: 2.5,
    borderRadius: 1.5,
  },
});

// Static sound wave bars. Previously these drove 5 looping Animated.timing
// sequences (useNativeDriver:false) that issued JS-side inline style writes
// ~60fps whenever voice mode was active. On iOS, those continuous layout
// recalculations were the source of the deep main-thread CALayer geometry
// traversal seen in the 0x8BADF00D watchdog crash when the app was
// backgrounded with voice mode active. The bars are now static Views with
// fixed heights.
const STATIC_BAR_HEIGHTS = [6, 10, 14, 9, 7];
function SoundWaveBars({ color }) {
  return (
    <View style={styles.soundWaveContainer}>
      {STATIC_BAR_HEIGHTS.map((height, index) => (
        <View
          key={index}
          style={[
            styles.soundBar,
            { height, backgroundColor: color },
          ]}
        />
      ))}
    </View>
  );
}

function VoiceModeButton({ 
  isDisabled = false,
  conversationId = null,
  deckTarget,
}) {
  const { t } = useTranslation();
  const { colors, accentBackgroundColor } = useTheme();
  // Use the theme's paired action colors unless the user chose a custom accent.
  const hasCustomAccent = accentBackgroundColor && accentBackgroundColor !== 'multicolor';
  const buttonColor = hasCustomAccent ? colors.accent_color_primary : colors.solid_color_toast;
  const barColor = hasCustomAccent ? colors.accent_text_color : colors.text_color_alt;
  const requireAuth = useRequireAuth();
  const isActive = useVoiceModeStore(state => state.isActive);
  const { startVoiceMode } = useVoiceModeStore(state => state.actions);
  
  // Animation refs
  const pulseScaleAnim = useRef(new Animated.Value(1)).current;
  const pulseOpacityAnim = useRef(new Animated.Value(0)).current;
  const hoverScaleAnim = useRef(new Animated.Value(1)).current;

  // Subtle pulse ring animation only when active
  useEffect(() => {
    let animation;
    
    if (isActive) {
      animation = Animated.loop(
        Animated.parallel([
          Animated.sequence([
            Animated.timing(pulseScaleAnim, {
              toValue: 1.4,
              duration: 2000,
              easing: Easing.out(Easing.ease),
              useNativeDriver: true,
            }),
            Animated.timing(pulseScaleAnim, {
              toValue: 1,
              duration: 0,
              useNativeDriver: true,
            }),
          ]),
          Animated.sequence([
            Animated.timing(pulseOpacityAnim, {
              toValue: 0.4,
              duration: 0,
              useNativeDriver: true,
            }),
            Animated.timing(pulseOpacityAnim, {
              toValue: 0,
              duration: 2000,
              easing: Easing.out(Easing.ease),
              useNativeDriver: true,
            }),
          ]),
        ])
      );
      animation.start();
    } else {
      pulseScaleAnim.setValue(1);
      pulseOpacityAnim.setValue(0);
    }
    
    return () => animation?.stop();
  }, [isActive, pulseOpacityAnim, pulseScaleAnim]);

  const handlePress = async () => {
    if (isDisabled) return;
    // Voice mode connects to a paid realtime backend — gate before we even
    // ask for microphone permission so logged-out visitors aren't prompted
    // for an OS permission they won't be able to use.
    const gated = requireAuth(() => true, {
      source: 'composer_voice',
      title: t('chatInput.authCta.voiceTitle'),
      subtitle: t('chatInput.authCta.voiceSubtitle'),
      icon: 'waveform',
    });
    if (gated !== true) return;
    if (!requirePlusForVoice({
      title: t('chatInput.voice.plusRequiredTitle'),
      subtitle: t('chatInput.voice.plusRequiredSubtitle'),
    })) return;
    // On native, request microphone permission in the same gesture as the tap.
    if (Platform.OS === 'android') {
      try {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.RECORD_AUDIO,
          {
            title: t('chatInput.voice.microphoneAccessTitle'),
            message: t('chatInput.voice.microphoneMessage'),
            buttonNeutral: t('chatInput.voice.askMeLater'),
            buttonNegative: t('chatInput.voice.deny'),
            buttonPositive: t('chatInput.voice.allow'),
          }
        );
        if (granted !== PermissionsAndroid.RESULTS.GRANTED) {
          if (granted === PermissionsAndroid.RESULTS.NEVER_ASK_AGAIN) {
            Alert.alert(
              t('chatInput.voice.microphoneNeededTitle'),
              t('chatInput.voice.microphoneNeededSettings'),
              [{ text: t('common.ok') }]
            );
          } else {
            Alert.alert(
              t('chatInput.voice.microphoneNeededTitle'),
              t('chatInput.voice.microphoneNeededPrompt'),
              [{ text: t('common.ok') }]
            );
          }
          return;
        }
      } catch (err) {
        console.warn('[VoiceModeButton] Android permission request failed:', err);
        Alert.alert(t('common.error'), t('chatInput.voice.microphoneRequestFailed'));
        return;
      }
    } else if (Platform.OS === 'ios' || Platform.OS === 'macos') {
      try {
        const { status } = await requestRecordingPermissionsAsync();
        if (status !== 'granted') {
          Alert.alert(
            t('chatInput.voice.microphoneNeededTitle'),
            Platform.OS === 'macos' ? 'Allow Gab AI microphone access in System Settings > Privacy & Security > Microphone.' : t('chatInput.voice.microphoneNeededSettingsIos'),
            Platform.OS === 'macos' ? [
              { text: t('common.cancel'), style: 'cancel' },
              { text: t('chatInput.dictate.openSettings'), onPress: () => require('@/lib/macAudioRecording').openMicrophoneSettings().catch(() => {}) },
            ] : [{ text: t('common.ok') }]
          );
          return;
        }
      } catch (err) {
        console.warn('[VoiceModeButton] Permission request failed:', err);
        Alert.alert(t('common.error'), t('chatInput.voice.microphoneRequestFailed'));
        return;
      }
    }
    const effectiveConversationId = isDraftComposerConversationId(conversationId) ? null : conversationId;
    if (deckTarget) startVoiceMode(effectiveConversationId, { deckTarget });
    else startVoiceMode(effectiveConversationId);
  };

  const handleHoverIn = () => {
    Animated.spring(hoverScaleAnim, {
      toValue: 1.05,
      friction: 10,
      tension: 200,
      useNativeDriver: true,
    }).start();
  };

  const handleHoverOut = () => {
    Animated.spring(hoverScaleAnim, {
      toValue: 1,
      friction: 10,
      tension: 200,
      useNativeDriver: true,
    }).start();
  };
  
  const tooltipLabel = t('chatInput.voice.voiceMode');

  return (
    <Tooltip label={tooltipLabel} keyboardShortcut="⌘⇧V" disabled={isActive}>
      <Animated.View style={{ transform: [{ scale: hoverScaleAnim }] }}>
        <Pressable 
          style={[
            styles.button,
            isDisabled && styles.buttonDisabled,
          ]}
          onPress={handlePress}
          onHoverIn={handleHoverIn}
          onHoverOut={handleHoverOut}
          disabled={isDisabled}
          accessibilityLabel={tooltipLabel}
          accessibilityRole="button"
          accessibilityState={{ disabled: isDisabled }}
        >
          {/* Subtle pulse ring - only when active */}
          {isActive && (
            <Animated.View
              style={[
                styles.pulseRing,
                {
                  borderColor: buttonColor,
                  transform: [{ scale: pulseScaleAnim }],
                  opacity: pulseOpacityAnim,
                }
              ]}
              pointerEvents="none"
            />
          )}

          <View style={[styles.innerContainer, { backgroundColor: buttonColor }]}>
            <SoundWaveBars color={barColor} />
          </View>
        </Pressable>
      </Animated.View>
    </Tooltip>
  );
}

export default React.memo(VoiceModeButton);
